import { useEffect, useRef, useState } from 'react'
import { Grid, TextField, FormControl, InputLabel, Select, InputAdornment, makeStyles, Container, FormHelperText } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import ButtonSp from '../ui/ButtonSp'
import { DatePicker } from '@material-ui/pickers'
import EventIcon from '@material-ui/icons/Event'
import ReCAPTCHA from 'react-google-recaptcha'
import SuccessModal from '../common/SuccessModal'
import dayjs from 'dayjs'
import Uploader from '../ui/Uploader'
import clsx from 'clsx'
import Axios from 'axios'
import getConfig from 'next/config'
import ProtectedByRecaptcha from '../common/ProtectedByRecaptcha'
import { useTranslation } from 'next-i18next'

const { publicRuntimeConfig } = getConfig()

const useStyles = makeStyles(theme => ({
  applyFormOuter: {
    ...theme.homeStyle.section,
    backgroundColor: '#fafafa',
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 0,
      paddingRight: 0,
    }
  },
  applyFormInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  fieldBox: {
    padding: `0 ${theme.spacing(2)}px`,
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    }
  },
  field: {
    textAlign: 'left',
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white'
    }
  },
  whiteBg: {
    backgroundColor: 'white',
  },
  button: {
    ...theme.homeStyle.button,
  },
  protected: {
    marginBottom: theme.spacing(2),
  }
}))

const getBase64 = (file) => {
  return new Promise((resolve) => {
    let reader = new FileReader()
    reader.onloadend = () => {
      resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })
}

const ApplyForm = ({selectedPosition,positions}) => {

  const { register, handleSubmit, control, setValue, errors, reset } = useForm()
  const recaptchaRef = useRef(null)
  const [isCaptchaExecuting,setCaptchaExecuting] = useState(false)
  const classes = useStyles()
  const [isSuccessModalOpen,setSuccessModalOpen] = useState(false)
  const [isErrorModalOpen,setErrorModalOpen] = useState(false)
  const [isUploaderError,setUploaderError] = useState(false)
  const [filesState,setFilesState] = useState([])
  const [percentUpload,setPercentUpload] = useState(-1)

  const { t } = useTranslation('career')

  useEffect(() => {
    setValue('position',selectedPosition ? selectedPosition:'')
  },[selectedPosition])

  const onSubmit = handleSubmit(async (data) => {
    try {
      setCaptchaExecuting(true)
      const token = await recaptchaRef.current.executeAsync()
      const { data: { success: captchaSuccess } } = await Axios.post('/api/recaptcha/validate',{ recaptchaToken: token })
      if (captchaSuccess) {
        let sendingData = {}
        Object.keys(data).forEach(key => {
          if (key === 'startDate') sendingData.startDate = dayjs(data.startDate)?.format('YYYY-MM-DD')
          else sendingData[key] = data[key]
        })
        // files to base64
        let sendingFiles = []

        if (!filesState[0].type.includes('jpg') && !filesState[0].type.includes('jpeg') && !filesState[0].type.includes('png') && !filesState[0].type.includes('pdf')) {
          setErrorModalOpen(true)

          return
        }

        const base64 = await getBase64(filesState[0]) 
        sendingFiles.push(base64)

        sendingData.files = sendingFiles
        sendingData.dontSendEmail = publicRuntimeConfig.applyJobDisableSendEmail === 'true'
        const { data: { status } } = await Axios.post('/api/career/apply', sendingData, {
          onUploadProgress: progressEvent => {
            setPercentUpload(Math.round( (progressEvent.loaded * 100) / progressEvent.total ))
          }
        })
        if (status === 'success') {
          setSuccessModalOpen(true)
          reset()
          setFilesState([])
        } else {
          setErrorModalOpen(true)
        }
      } else {
        setErrorModalOpen(true)
      }
    } catch (err) {
      setErrorModalOpen(true)
    } finally {
      recaptchaRef.current.reset()
      setCaptchaExecuting(false)
    }
  })

  return (
    <div className={classes.applyFormOuter} id="apply-form">
      <Container>
      <form className={classes.applyFormInner} onSubmit={onSubmit}>
        <h2>{t('form.title')}</h2>
        <Grid container>
          <Grid item xs={12} sm={6} className={classes.fieldBox}>
            <TextField
              label={t('form.name')}
              name="nameSurname"
              variant="outlined"
              margin="normal"
              fullWidth
              className={classes.field}
              inputRef={register({ required: true })}
              error={errors.nameSurname}
              helperText={errors.nameSurname && t('form.error.please-fill-name')}
              placeholder={t('form.name-p')}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.fieldBox}>
            <TextField
              label={t('form.email')}
              name="email"
              variant="outlined"
              margin="normal"
              fullWidth
              className={classes.field}
              inputRef={register({ required: true, pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/ })}
              error={errors.email}
              helperText={errors.email && t('form.error.please-fill-email-correctly')}
              placeholder={t('form.email-p')}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.fieldBox}>
            <TextField
              label={t('form.phone')}
              name="phone"
              variant="outlined"
              margin="normal"
              type="number"
              fullWidth
              className={classes.field}
              inputRef={register({ required: true, pattern: /^0\d{8,9}$/ })}
              error={errors.phone}
              helperText={errors.phone && t('form.error.please-fill-phone-correctly')}
              placeholder={t('form.phone-p')}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.fieldBox}>
            <FormControl variant="outlined" fullWidth margin="normal">
              <InputLabel error={errors.position}>
                {t('form.position')}
              </InputLabel>
              <Controller
                as={
                  <Select
                    label={t('form.position')}
                    fullWidth
                    className={clsx(classes.field,classes.whiteBg)}
                    native
                    error={errors.position}
                  >
                    <option value="" disabled />
                    {
                      positions.map((positionName) => (
                        <option value={positionName} key={positionName}>{positionName}</option>
                      ))
                    }
                  </Select>
                }
                control={control}
                name="position"
                defaultValue=""
                rules={{
                  required: true
                }}
              />
              {errors.position && <FormHelperText error>{t('form.error.please-select-position')}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} className={classes.fieldBox}>
            <TextField
              label={t('form.expected-salary')}
              name="expectedSalary"
              type="number"
              variant="outlined"
              margin="normal"
              fullWidth
              className={classes.field}
              inputRef={register({ required: true })}
              error={errors.expectedSalary}
              helperText={errors.expectedSalary && t('form.error.please-fill-expected-salary')}
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.fieldBox}>
              <Controller
                as={
                  <DatePicker
                    inputVariant="outlined"
                    label={t('form.starting-date')}
                    fullWidth
                    format="LL"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <EventIcon color="disabled" />
                        </InputAdornment>
                      )
                    }}
                    margin="normal"
                    style={{
                      backgroundColor: 'white'
                    }}
                  />
                }
                control={control}
                name="startDate"
                defaultValue={dayjs()}
              />
          </Grid>
          <Grid item xs={12} className={classes.fieldBox}>
            <TextField
              label={t('form.why')}
              multiline
              rows={3}
              variant="outlined"
              type="text"
              margin="normal"
              fullWidth
              className={classes.field}
              name="reasonToHire"
              inputRef={register({ required: true, minLength: 30 })}
              error={errors.reasonToHire}
              helperText={errors.reasonToHire && t('form.error.please-fill-why')}
              placeholder={t('form.why-p')}
            />
          </Grid>
          <Grid item xs={12} className={classes.fieldBox}>
            <TextField
              label={t('form.portfolio')}
              variant="outlined"
              type="text"
              margin="normal"
              fullWidth
              className={classes.field}
              name="portfolioLink"
              inputRef={register({
                validate: value => value === '' || /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(value)
              })}
              error={errors.portfolioLink}
              helperText={errors.portfolioLink && t('form.error.please-fill-url-correctly')}
            />
          </Grid>
          <Grid item xs={12} className={classes.fieldBox}>
            <dl style={{ width: '100%', textAlign: 'left', marginLeft: 8 }}>{t('form.resume')}</dl>
            <Uploader
              filesState={filesState}
              setFilesState={setFilesState}
              setUploaderError={setUploaderError}
            />
            <div style={{ height: 24 }} />
          </Grid>
        </Grid>
        <ProtectedByRecaptcha className={classes.protected} />
        <ButtonSp type="submit" className={classes.button} loading={isCaptchaExecuting} disabled={isUploaderError}>{t('apply')}</ButtonSp>
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={publicRuntimeConfig.recaptcha2Key}
          badge="bottomleft"
          style={{
            zIndex: 999,
          }}
        />
      </form>
      </Container>
      <SuccessModal
        isOpen={percentUpload > -1 && percentUpload < 100}
        setOpen={(bool) => bool}
        title={`Uploading: ${percentUpload}%`}
        subtitle={''}
        type="progress"
      />
      <SuccessModal
        isOpen={isSuccessModalOpen}
        setOpen={setSuccessModalOpen}
        title={t('form.success-modal-title')}
        subtitle={t('form.success-modal-subtitle')}
      />
      <SuccessModal
        isOpen={isErrorModalOpen}
        setOpen={setErrorModalOpen}
        title={t('form.an-error-occurred')}
        subtitle={t('form.please-try-again-later')}
        type="error"
      />
    </div>
  )
}

export default ApplyForm